import { createFeatureSelector, createSelector } from '@ngrx/store'
import { last } from 'lodash/fp'
import { random as _random } from 'lodash/fp'
import { v4 as uuidV4 } from 'uuid'

import { ScriptingLanguage } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { AiAnalystUtils } from '@flowx/designer/modules/new-ai/agents/analyst/services/analyst.service'
import {
  selectMergedRouteData,
  selectRouteParams,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

// eslint-disable-next-line import/no-cycle
import { aiFeatureKey } from './ai-store.module'
import { AiState } from './ai.state'

import { DesignerServiceNew } from '../agents/designer/services/designer-new.service'
import { DesignerService } from '../agents/designer/services/designer.service'
import {
  AiAgent,
  FileExtension,
  FileMimeType,
  MessageType,
  UploadContextFile,
  WindowMode,
  imageFileTypes,
  scriptingLanguageWithGeneration,
} from '../common/ai.dictionary'

const selectFeature = createFeatureSelector<AiState>(aiFeatureKey)

// #region business rules
const generatedBusinessRule = createSelector(selectFeature, (state) => state.generatedBusinessRule)
const generatedTestData = createSelector(selectFeature, (state) => state.generatedTestData)
const generatedExpression = createSelector(selectFeature, (state) => state.generatedExpression)
const generatedImprovedCode = createSelector(selectFeature, (state) => state.generatedImprovedCode)
const generatedAddComment = createSelector(selectFeature, (state) => state.generatedAddComment)
const generatedGatewayCondition = createSelector(
  selectFeature,
  (state) => state.generatedGatewayCondition
)
const generatedFixWarningsBusinessRule = createSelector(
  selectFeature,
  (state) => state.generatedFixWarningsBusinessRule
)
const generatedFixErrorBusinessRule = createSelector(
  selectFeature,
  (state) => state.generatedFixErrorBusinessRule
)

const businessRuleContext = createSelector(selectFeature, (state) => state.businessRuleContext)
const reusableFunctionContext = createSelector(
  selectFeature,
  (state) => state.reusableFunctionContext
)
const expressionContext = createSelector(selectFeature, (state) => state.expressionContext)
const codeSelectionContext = createSelector(selectFeature, (state) => state.codeSelectionContext)

const canGenerateBusinessRule = createSelector(businessRuleContext, (businessRuleContext) =>
  scriptingLanguageWithGeneration.includes(businessRuleContext?.language as ScriptingLanguage)
)
const canGenerateExpression = createSelector(
  expressionContext,
  (expressionContext) => !expressionContext?.expression
)

const hasBusinessProcess = createSelector(selectFeature, () => {
  const contextAnalyst = AiAnalystUtils.getInitialProcessContext()
  const totalNodes = contextAnalyst
    ? contextAnalyst.sl?.reduce((sum, section) => sum + (section.nl?.length || 0), 0)
    : 0
  return totalNodes > 0
})

const getScreenContext = createSelector(selectFeature, (state) => state.screen_context)

const routeToAgent = createSelector(
  selectFeature,
  (state) =>
    state.pendingRouteToAssistant !== null ||
    state.pendingRouteToDesigner !== null ||
    state.pendingRouteToAnalyst !== null ||
    state.pendingRouteToDeveloper !== null ||
    state.pendingRouteToCoordinator !== null ||
    state.pendingRouteToArchitect !== null ||
    state.pendingRouteToIntegrator !== null ||
    state.pendingRouteToAuditor !== null
)

const hasBusinessRuleDraft = createSelector(
  generatedBusinessRule,
  (generatedBusinessRule) => !!generatedBusinessRule
)

const hasTestDataDraft = createSelector(
  generatedTestData,
  (generatedTestData) => !!generatedTestData
)
const hasExpressionDraft = createSelector(
  generatedExpression,
  (generatedExpression) => !!generatedExpression
)
const hasImprovedCodeDraft = createSelector(
  generatedImprovedCode,
  (generatedImprovedCode) => !!generatedImprovedCode
)
const hasAddCommentDraft = createSelector(
  generatedAddComment,
  (generatedAddComment) => !!generatedAddComment
)
const hasGatewayConditionDraft = createSelector(
  generatedGatewayCondition,
  (generatedGatewayCondition) => !!generatedGatewayCondition
)
const hasFixWarningsBusinessRuleDraft = createSelector(
  generatedFixWarningsBusinessRule,
  (generatedFixWarningsBusinessRule) => !!generatedFixWarningsBusinessRule
)
const generatedCustomComponent = createSelector(
  selectFeature,
  (state) => state.generatedCustomComponent
)
const customComponentContext = createSelector(
  selectFeature,
  (state) => state.customComponentContext
)
const hasCustomComponentDraft = createSelector(
  generatedCustomComponent,
  (generatedCustomComponent) => !!generatedCustomComponent
)

const hasFixErrorBusinessRuleDraft = createSelector(
  generatedFixErrorBusinessRule,
  (generatedFixErrorBusinessRule) => !!generatedFixErrorBusinessRule
)
const hasDeveloperAgentDraft = createSelector(
  hasBusinessRuleDraft,
  hasExpressionDraft,
  hasFixWarningsBusinessRuleDraft,
  hasFixErrorBusinessRuleDraft,
  hasImprovedCodeDraft,
  hasAddCommentDraft,
  hasGatewayConditionDraft,
  (...hasDraftItems) => hasDraftItems.some((draftItem) => !!draftItem)
)

const businessRuleLanguageAiOptions = createSelector(
  businessRuleContext,
  canGenerateBusinessRule,
  (businessRuleContext, canGenerateBusinessRule) => ({
    disabled: !canGenerateBusinessRule,
    tooltip:
      businessRuleContext && !canGenerateBusinessRule
        ? `${businessRuleContext?.language} is not currently supported by our AI agent.`
        : null,
  })
)

export const AiDeveloperSelectors = {
  generatedBusinessRule,
  generatedExpression,
  generatedImprovedCode,
  generatedAddComment,
  generatedFixWarningsBusinessRule,
  generatedFixErrorBusinessRule,
  businessRuleContext,
  reusableFunctionContext,
  expressionContext,
  codeSelectionContext,
  canGenerateBusinessRule,
  canGenerateExpression,
  hasBusinessRuleDraft,
  hasExpressionDraft,
  hasImprovedCodeDraft,
  hasAddCommentDraft,
  hasGatewayConditionDraft,
  hasFixWarningsBusinessRuleDraft,
  hasFixErrorBusinessRuleDraft,
  hasDeveloperAgentDraft,
  businessRuleLanguageAiOptions,
  generatedTestData,
  generatedGatewayCondition,
}
// #endregion

const permissions = createSelector(selectFeature, (state) => state.permissions)
const fabPosition = createSelector(selectFeature, (state) => state.fabPosition)
const fileUploadConfig = createSelector(selectFeature, (state) => state.fileUploadConfig)
const minimized = createSelector(selectFeature, (state) => state.minimized)
const zenMode = createSelector(selectFeature, (state) => state.zenMode)
const selectedAgent = createSelector(selectFeature, (state) => state.selectedAgent)
const selectedDocument = createSelector(selectFeature, (state) => state.selectedDocument)

const currentEnumeration = createSelector(selectFeature, (state) => state.currentEnumeration)
const currentNotification = createSelector(selectFeature, (state) => state.currentNotification)
const currentUI = createSelector(selectFeature, (state) => state.currentUI)
const currentFullUI = createSelector(selectFeature, (state) => state.currentFullUI)
const currentDataModel = createSelector(selectFeature, (state) => state.currentDataModel)

const generatedEnumeration = createSelector(selectFeature, (state) => state.generatedEnumeration)
const generatedNotification = createSelector(selectFeature, (state) => state.generatedNotification)
const generatedImage = createSelector(selectFeature, (state) => state.generatedImage)
const generatedDataModel = createSelector(selectFeature, (state) => state.generatedDataModel)

const draftMessage = createSelector(selectFeature, (state) => state.draftMessage)
const initialMessage = createSelector(selectFeature, (state) => state.initialMessage)
const selectedDocumentContext = createSelector(
  selectFeature,
  (state) => state.selectedDocumentContext
)
const selectedDesignerTemplateConfigId = createSelector(
  selectFeature,
  (state) => state.selectedDesignerTemplateConfigId
)
const selectedDesignerUserTaskAreaId = createSelector(
  selectFeature,
  (state) => state.selectedDesignerUserTaskAreaId
)

const generatedPrompt = createSelector(selectFeature, (state) => state.generatedPrompt)
const generatedAgentWorkflow = createSelector(
  selectFeature,
  (state) => state.generatedAgentWorkflow
)
const generatedDatasource = createSelector(selectFeature, (state) => state.generatedDatasource)
const currentSystemContext = createSelector(selectFeature, (state) => state.currentSystemContext)
const selectedDesignerNodeId = createSelector(
  selectFeature,
  (state) => state.selectedDesignerNodeId
)
const sessionId = createSelector(selectFeature, (state) => state.sessionId)
const lastRequestId = createSelector(selectFeature, (state) => state.lastRequestId)
const isFileUploadAllowed = createSelector(selectFeature, (state) => state.isFileUploadAllowed)
const isChatConversationAllowed = createSelector(
  selectFeature,
  (state) => state.isChatConversationAllowed
)
const messages = createSelector(selectFeature, (state) => state.messages)
const logs = createSelector(selectFeature, (state) => state.logs)
const reasoningLogs = createSelector(selectFeature, (state) => state.reasoningLogs)
const lastMessage = createSelector(messages, (messages) => last(messages))
const isLastMsgError = createSelector(
  lastMessage,
  (message) => message?.type === MessageType.ErrorMsg
)
const hasMessages = createSelector(messages, (messages) => messages.length > 0)
const uploadedFiles = createSelector(selectFeature, (state) => state.uploadedFiles)
const uploadingDocuments = createSelector(selectFeature, (state) => state.uploadingDocuments)
const pendingDocumentsLoading = createSelector(
  selectFeature,
  (state) => state.pendingDocumentsLoading
)

const currentDocuments = createSelector(selectFeature, (state) =>
  [...state.currentDocuments].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
    return dateB - dateA
  })
)

const pendingAiResponse = createSelector(selectFeature, (state) => state.pendingAiResponse)
const pendingFixWarningsResponse = createSelector(
  selectFeature,
  (state) => state.pendingFixWarningsResponse
)
const generatedAiRecommendation = createSelector(
  selectFeature,
  (state) => state.generatedAiRecommendation
)
const pendingFixErrorResponse = createSelector(
  selectFeature,
  (state) => state.pendingFixErrorResponse
)
const pendingKeepDraftSave = createSelector(selectFeature, (state) => state.pendingKeepDraftSave)
const streamingAiResponse = createSelector(selectFeature, (state) => state.streamingAiResponse)
const generatedProcess = createSelector(selectFeature, (state) => state.generatedProcess)
const temporaryProcess = createSelector(selectFeature, (state) => state.temporaryProcess)
const generatedSequenceProcess = createSelector(
  selectFeature,
  (state) => state.generatedSequenceProcess
)
const generatedProcessEntities = createSelector(
  selectFeature,
  (state) => state.generatedProcessEntities
)

const temporaryProcessEntities = createSelector(
  selectFeature,
  (state) => state.temporaryProcessEntities
)
const generatedUI = createSelector(selectFeature, (state) => state.generatedUI)
const generatedUINew = createSelector(selectFeature, (state) => state.generatedUINew)
const generatedUiFlow = createSelector(selectFeature, (state) => state.generatedUiFlow)
const generatedUiFlowTemplateConfigs = createSelector(
  selectFeature,
  (state) => state.generatedUiFlowTemplateConfigs
)
const selectedUiFlowSubtreeId = createSelector(
  selectFeature,
  (state) => state.selectedUiFlowSubtreeId
)
const hasUiFlowDraft = createSelector(generatedUiFlow, (draft) => !!draft)
const generatedLocationId = createSelector(selectFeature, (state) => state.generatedLocationId)
const generatedUIDataModelChanges = createSelector(
  selectFeature,
  (state) => state.generatedUIDataModelChanges
)

const hasDesignerAiPermission = createSelector(
  permissions,
  (permissions) => permissions.showDesignerAgent
)
const hasDeveloperAiPermission = createSelector(
  permissions,
  (permissions) => permissions.showDeveloperAgent
)
const hasAnalystAiPermission = createSelector(
  permissions,
  (permissions) => permissions.showAnalystAgent
)
const hasAssistantAiPermission = createSelector(
  permissions,
  (permissions) => permissions.showAssistantAgent
)
const isMinimized = createSelector(minimized, (minimized) => minimized)

const isExpanded = createSelector(minimized, (minimized) => !minimized)
const isZenMode = createSelector(zenMode, (zenMode) => zenMode)
const windowMode = createSelector(selectFeature, (state) =>
  state.messages?.length && state.selectedAgent
    ? WindowMode.AiChat
    : state.draftMessage && !state.initialMessage
      ? WindowMode.Loading
      : WindowMode.CommandPalette
)
const isChatWindowMode = createSelector(
  windowMode,
  (windowMode) => windowMode === WindowMode.AiChat
)
const isCommandPaletteWindowMode = createSelector(
  windowMode,
  (windowMode) => windowMode === WindowMode.CommandPalette
)

const isInitialLoadingWindowMode = createSelector(
  windowMode,
  (windowMode) => windowMode === WindowMode.Loading
)
const pageAgent = createSelector(selectMergedRouteData, (mergedRouteData) => mergedRouteData.agent)

const pageRouteParams = createSelector(selectRouteParams, (routeParams) => routeParams)

const pageAgentActions = createSelector(
  selectMergedRouteData,
  (mergedRouteData) => mergedRouteData.agentActions || null
)

const pageLocation = createSelector(
  selectMergedRouteData,
  (mergedRouteData) => mergedRouteData.location || null
)

const activeLocationAgent = createSelector(
  businessRuleContext,
  expressionContext,
  (businessRuleContext, expressionContext) =>
    businessRuleContext || expressionContext ? AiAgent.DEVELOPER : null
)

const contextAgent = createSelector(
  selectedAgent,
  activeLocationAgent,
  pageAgent,
  (selectedAgent, activeLocationAgent, pageAgent) =>
    selectedAgent || activeLocationAgent || pageAgent
)
const uploadedImageFiles = createSelector(uploadedFiles, (uploadedFiles) =>
  uploadedFiles.filter((file): file is UploadContextFile =>
    imageFileTypes.includes(file.type as FileMimeType)
  )
)
const hasUploadedFiles = createSelector(uploadedFiles, (uploadedFiles) => !!uploadedFiles.length)
const uploadedBpmnFiles = createSelector(uploadedFiles, (uploadedFiles) =>
  uploadedFiles.filter(
    (file): file is UploadContextFile =>
      file.type === FileMimeType.BPMN || file.name.endsWith(FileExtension.BPMN)
  )
)
const allowedImageFileTypes = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.imageUpload?.fileType || []
)
const allowedBpmnFileTypes = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.bpmnUpload?.fileType || []
)
const maxAllowedImageFiles = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.imageUpload?.maxFileNumber || 1
)
const isMultipleImageUploadAllowed = createSelector(
  maxAllowedImageFiles,
  (maxAllowedImageFiles) => maxAllowedImageFiles > 1
)

const allowedProcessFileTypes = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.processUpload?.fileType || []
)

const maxAllowedProcessFiles = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.processUpload?.maxFileNumber || 1
)
const isMultipleProcessUploadAllowed = createSelector(
  maxAllowedProcessFiles,
  (maxAllowedProcessFiles) => maxAllowedProcessFiles > 1
)

const allowedDocumentFileTypes = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.documentUpload?.fileType || []
)

const maxAllowedDocumentFiles = createSelector(
  fileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.documentUpload?.maxFileNumber || 1
)
const isMultipleDocumentUploadAllowed = createSelector(
  maxAllowedDocumentFiles,
  (maxAllowedDocumentFiles) => maxAllowedDocumentFiles > 1
)
const agentFileUploadConfig = createSelector(
  fileUploadConfig,
  selectedAgent,
  (fileUploadConfig, agent) =>
    fileUploadConfig?.fileUpload?.[agent] || fileUploadConfig?.fileUpload?.default
)
const allowedFileTypes = createSelector(
  agentFileUploadConfig,
  selectedAgent,
  (fileUploadConfig) => fileUploadConfig?.fileType || []
)
const maxAllowedFiles = createSelector(
  agentFileUploadConfig,
  (fileUploadConfig) => fileUploadConfig?.maxFileNumber || 1
)
const isMultipleUploadAllowed = createSelector(
  maxAllowedFiles,
  (maxAllowedFiles) => maxAllowedFiles > 1
)
const isUploadingDocuments = createSelector(
  uploadingDocuments,
  (uploadingDocuments) => !!uploadingDocuments.length
)
const hasSelectedDocument = createSelector(
  selectedDocument,
  (selectedDocument) => !!selectedDocument
)
const hasSelectedDocumentContext = createSelector(
  selectedDocumentContext,
  (selectedDocumentContext) => !!selectedDocumentContext
)

const breadcrumbs = createSelector(selectFeature, (state) => [
  ...(state.selectedAgent ? [state.selectedAgent] : []),
])

const requestId = createSelector(
  messages,
  (messages) =>
    [...messages].reverse().find((message) => message.context?.request_id)?.context?.request_id
)
const hasProcessDraft = createSelector(generatedProcess, (generatedProcess) => !!generatedProcess)
const hasUiDraft = createSelector(generatedUI, (generatedUI) => !!generatedUI)
const hasUINewDraft = createSelector(generatedUINew, (generatedUINew) => !!generatedUINew)
const editDiffAppliedConfig = createSelector(selectFeature, (state) => state.editDiffAppliedConfig)
const generatedEditDiff = createSelector(selectFeature, (state) => state.generatedEditDiff)
const hasEditDiffDraft = createSelector(
  editDiffAppliedConfig,
  (editDiffAppliedConfig) => !!editDiffAppliedConfig
)
const hasImageDraft = createSelector(generatedImage, (generatedImage) => !!generatedImage)

const hasEnumerationDraft = createSelector(
  generatedEnumeration,
  (generatedEnumeration) => !!generatedEnumeration
)

const hasNotificationDraft = createSelector(
  generatedNotification,
  (generatedNotification) => !!generatedNotification
)

const hasUiDataModelDraft = createSelector(
  generatedUIDataModelChanges,
  (generatedUIDataModelChanges) => !!generatedUIDataModelChanges
)

const hasDataModelDraft = createSelector(
  generatedDataModel,
  (generatedDataModel) => !!generatedDataModel
)

const hasAgentDraft = createSelector(
  generatedAgentWorkflow,
  (generatedAgentWorkflow) => !!generatedAgentWorkflow
)

const hasDatasourceDraft = createSelector(
  generatedDatasource,
  (generatedDatasource) => !!generatedDatasource
)

const generatingUi = createSelector(selectFeature, (state) => state.generatingUi)
const generatingPlan = createSelector(selectFeature, (state) => state.generatingPlan)

const hasDraft = createSelector(
  hasProcessDraft,
  hasUiDraft,
  hasImageDraft,
  hasEnumerationDraft,
  hasNotificationDraft,
  hasUINewDraft,
  hasEditDiffDraft,
  hasUiDataModelDraft,
  hasBusinessRuleDraft,
  hasFixWarningsBusinessRuleDraft,
  hasFixErrorBusinessRuleDraft,
  hasExpressionDraft,
  hasImprovedCodeDraft,
  hasAddCommentDraft,
  hasDataModelDraft,
  hasAgentDraft,
  hasDatasourceDraft,
  hasGatewayConditionDraft,
  hasUiFlowDraft,
  hasCustomComponentDraft,
  (...draftFlags) => draftFlags.some((draft) => !!draft)
)

const hasAudit = createSelector(selectFeature, (state) => state.hasAudit)

const generatedProcessPreviewHeader = createSelector(
  selectFeature,
  (state) => state.generatedProcessPreviewHeader
)
const generatedProcessPreviewHeaderContainerTransform = createSelector(
  generatedProcessPreviewHeader,
  (generatedProcessPreviewHeader) => generatedProcessPreviewHeader?.containerTransform || null
)
const generatedProcessPreviewHeaderContentTransform = createSelector(
  generatedProcessPreviewHeader,
  (generatedProcessPreviewHeader) => generatedProcessPreviewHeader?.contentTransform || null
)
const isBreadcrumbsBackDisabled = createSelector(
  pendingAiResponse,
  hasDraft,
  activeLocationAgent,
  expressionContext,
  (pendingAiResponse, hasDraft, activeLocationAgent, expressionContext) =>
    pendingAiResponse ||
    hasDraft ||
    (activeLocationAgent === AiAgent.DEVELOPER && !!expressionContext)
)
const isUploadAllowed = createSelector(
  isChatWindowMode,
  selectedAgent,
  permissions,
  (isChatWindowMode, selectedAgent, permissions) =>
    (isChatWindowMode &&
      [AiAgent.ANALYST, AiAgent.DESIGNER, AiAgent.DEVELOPER, AiAgent.AUDITOR].includes(
        selectedAgent
      ) &&
      permissions.canUploadDocuments) ||
    true
)
const isUploadDisabled = createSelector(
  isFileUploadAllowed,
  hasSelectedDocument,
  hasUploadedFiles,
  pendingAiResponse,
  hasDraft,
  (isFileUploadAllowed, hasSelectedDocument, hasUploadedFiles, pendingAiResponse, hasDraft) =>
    !isFileUploadAllowed || hasSelectedDocument || hasUploadedFiles || pendingAiResponse || hasDraft
)
const isPendingOrHasDraft = createSelector(
  pendingAiResponse,
  hasDraft,
  (pendingAiResponse, hasDraft) => pendingAiResponse || hasDraft
)

const canCloseChat = createSelector(hasDraft, (hasDraft) => !hasDraft)

// const canCloseChat = createSelector(
//   isPendingOrHasDraft,
//   (isPendingOrHasDraft) => !isPendingOrHasDraft
// )

const isGenerateFromDocumentContextDisabled = createSelector(
  pendingAiResponse,
  hasDraft,
  hasSelectedDocument,
  (pendingAiResponse, hasDraft, hasSelectedDocument) =>
    pendingAiResponse || hasDraft || !hasSelectedDocument
)
const isPlanEnabled = createSelector(selectFeature, (state) => state.isPlanEnabled)
const isPlanSidebarVisible = createSelector(selectFeature, (state) => state.isPlanSidebarVisible)
const isChatSidebarVisible = createSelector(selectFeature, (state) => state.isChatSidebarVisible)
const isDocumentsSidebarVisible = createSelector(
  selectFeature,
  (state) => state.isDocumentsSidebarVisible
)
const isArchivedSidebarVisible = createSelector(
  selectFeature,
  (state) => state.isArchivedSidebarVisible
)

const selectedUiFlowPageIndex = createSelector(
  selectFeature,
  (state) => state.selectedUiFlowPageIndex
)

const draftTemplateConfig = createSelector(
  generatedUI,
  generatedUINew,
  generatedLocationId,
  editDiffAppliedConfig,
  selectedUiFlowPageIndex,
  (generatedUI, generatedUINew, generatedLocationId, editDiffAppliedConfig, pageIndex) => {
    if (editDiffAppliedConfig) {
      return editDiffAppliedConfig
    }
    if (generatedUI && generatedUI.sectionsDetails) {
      return DesignerService.mapAiUiToTemplateConfig(
        () => _random(Date.now(), Number.MAX_SAFE_INTEGER),
        uuidV4,
        generatedUI
      ) as TemplateConfigInterface
    }
    if (generatedUINew && generatedUINew.components) {
      // For multi-page UI Flows: filter to show only the selected page's component tree
      const pageRoots = generatedUINew.components.filter((c: any) => c.pId === 0)
      const selectedPage = pageRoots[pageIndex] || pageRoots[0]
      if (selectedPage && pageRoots.length > 1) {
        // Build a filtered component list containing only this page and its descendants
        const selectedPageId = selectedPage.id
        const pageComponents = new Set<number>([selectedPageId])
        // Iteratively collect all descendants
        let changed = true
        while (changed) {
          changed = false
          for (const c of generatedUINew.components) {
            if (!pageComponents.has(c.id) && pageComponents.has(c.pId)) {
              pageComponents.add(c.id)
              changed = true
            }
          }
        }
        const filteredConfig = {
          ...generatedUINew,
          components: generatedUINew.components.filter((c: any) => pageComponents.has(c.id)),
        }
        return DesignerServiceNew.mapAiUiToTemplateConfig(
          () => _random(Date.now(), Number.MAX_SAFE_INTEGER),
          uuidV4,
          filteredConfig
        ) as TemplateConfigInterface
      }
      return DesignerServiceNew.mapAiUiToTemplateConfig(
        () => _random(Date.now(), Number.MAX_SAFE_INTEGER),
        uuidV4,
        generatedUINew
      ) as TemplateConfigInterface
    }
    return null
  }
)

const isAiEnabled = createSelector(
  permissions,
  pageAgent,
  activeLocationAgent,
  (permissions, pageAgent, activeLocationAgent) =>
    permissions.showAssistantAgent ||
    (permissions.showAnalystAgent && pageAgent === AiAgent.ANALYST) ||
    (permissions.showDesignerAgent && pageAgent === AiAgent.DESIGNER) ||
    (permissions.showDeveloperAgent &&
      (activeLocationAgent === AiAgent.DEVELOPER || pageAgent === AiAgent.DEVELOPER))
)

const isAiInteractionAllowed = createSelector(
  selectFeature,
  (state) => state.isAiInteractionAllowed
)

const isAiFabVisible = createSelector(
  isAiEnabled,
  isAiInteractionAllowed,
  (isAiEnabled, isAiInteractionAllowed) => isAiEnabled && isAiInteractionAllowed
)

const startedFromSequence = createSelector(selectFeature, (state) => state.startedFromSequence)
const pendingRouteToAssistant = createSelector(
  selectFeature,
  (state) => state.pendingRouteToAssistant
)
const pendingRouteToDesigner = createSelector(
  selectFeature,
  (state) => state.pendingRouteToDesigner
)
const pendingRouteToAnalyst = createSelector(selectFeature, (state) => state.pendingRouteToAnalyst)
const pendingRouteToDeveloper = createSelector(
  selectFeature,
  (state) => state.pendingRouteToDeveloper
)
const pendingRouteToCoordinator = createSelector(
  selectFeature,
  (state) => state.pendingRouteToCoordinator
)
const pendingRouteToArchitect = createSelector(
  selectFeature,
  (state) => state.pendingRouteToArchitect
)
const pendingRouteToIntegrator = createSelector(
  selectFeature,
  (state) => state.pendingRouteToIntegrator
)
const pendingRouteToAuditor = createSelector(selectFeature, (state) => state.pendingRouteToAuditor)

const selectedPlan = createSelector(selectFeature, (state) => state.selectedPlan)
const currentAITaskMessage = createSelector(selectFeature, (state) => state.currentAITaskMessage)
const documentUploadMessage = createSelector(selectFeature, (state) => state.documentUploadMessage)
const waitingForHumanApproval = createSelector(
  selectFeature,
  (state) => state.waitingForHumanApproval
)
const generatedIntent = createSelector(selectFeature, (state) => state.generatedIntent)
const generatedActions = createSelector(selectFeature, (state) => state.generatedActions)
const autonomyLevel = createSelector(selectFeature, (state) => state.autonomyLevel)
const getAppId = createSelector(selectFeature, (state) => state.appId)
const getAppVersionId = createSelector(selectFeature, (state) => state.appVersionId)
const getProcessId = createSelector(selectFeature, (state) => state.processId)
const getProcessName = createSelector(selectFeature, (state) => state.processName)
const processWarningsNo = createSelector(selectFeature, (state) => state.process_warnings_no)
const processRules = createSelector(selectFeature, (state) => state.process_rules)
const processWarnings = createSelector(selectFeature, (state) => state.process_warnings)

const selectAutonomyLevel = createSelector(selectFeature, (state) => state.autonomyLevel)
const selectGeneratedUINew = createSelector(selectFeature, (state) => state.generatedUINew)
const designerVersion = createSelector(selectFeature, (state) => state.designerVersion)
const currentPlans = createSelector(selectFeature, (state) => state.currentPlans)
const currentPlanStep = createSelector(selectFeature, (state) => state.currentPlanStep)
export const AiSelectors = {
  ...AiDeveloperSelectors,
  selectFeature,
  permissions,
  hasAssistantAiPermission,
  hasAnalystAiPermission,
  hasDesignerAiPermission,
  hasDeveloperAiPermission,
  isMinimized,
  isExpanded,
  windowMode,
  hasTestDataDraft,
  draftMessage,
  initialMessage,
  isChatWindowMode,
  isCommandPaletteWindowMode,
  isInitialLoadingWindowMode,
  isPlanEnabled,
  isPlanSidebarVisible,
  isChatSidebarVisible,
  isDocumentsSidebarVisible,
  isArchivedSidebarVisible,
  selectedAgent,
  pageAgent,
  pageAgentActions,
  contextAgent,
  selectedDocument,
  selectedDocumentContext,
  selectedDesignerNodeId,
  selectedDesignerTemplateConfigId,
  selectedDesignerUserTaskAreaId,
  pageLocation,
  currentDataModel,
  currentEnumeration,
  currentNotification,
  currentUI,
  currentFullUI,
  hasSelectedDocument,
  hasSelectedDocumentContext,
  hasProcessDraft,
  messages,
  lastMessage,
  isLastMsgError,
  hasUploadedFiles,
  uploadedImageFiles,
  uploadedBpmnFiles,
  fileUploadConfig,
  agentFileUploadConfig,
  allowedImageFileTypes,
  allowedBpmnFileTypes,
  isMultipleImageUploadAllowed,
  allowedProcessFileTypes,
  maxAllowedProcessFiles,
  isMultipleProcessUploadAllowed,
  allowedDocumentFileTypes,
  isMultipleDocumentUploadAllowed,
  allowedFileTypes,
  isMultipleUploadAllowed,
  breadcrumbs,
  isBreadcrumbsBackDisabled,
  uploadingDocuments,
  isUploadingDocuments,
  isUploadDisabled,
  sessionId,
  lastRequestId,
  requestId,
  temporaryProcess,
  generatedProcess,
  generatedSequenceProcess,
  temporaryProcessEntities,
  generatedProcessEntities,
  generatedUI,
  generatedUINew,
  generatedUiFlow,
  generatedUiFlowTemplateConfigs,
  selectedUiFlowSubtreeId,
  generatedLocationId,
  editDiffAppliedConfig,
  generatedEditDiff,
  generatedUIDataModelChanges,
  canGenerateBusinessRule,
  generatedBusinessRule,
  generatedFixWarningsBusinessRule,
  canGenerateExpression,
  generatedExpression,
  generatedEnumeration,
  generatedNotification,
  generatedDataModel,
  generatedImage,
  pendingDocumentsLoading,
  currentDocuments,
  pendingAiResponse,
  pendingFixWarningsResponse,
  pendingFixErrorResponse,
  pendingKeepDraftSave,
  generatedAiRecommendation,
  streamingAiResponse,
  hasDraft,
  hasAudit,
  businessRuleLanguageAiOptions,
  hasBusinessRuleDraft,
  hasFixWarningsBusinessRuleDraft,
  hasExpressionDraft,
  generatedProcessPreviewHeader,
  generatedProcessPreviewHeaderContainerTransform,
  generatedProcessPreviewHeaderContentTransform,
  isUploadAllowed,
  fabPosition,
  canCloseChat,
  isGenerateFromDocumentContextDisabled,
  draftTemplateConfig,
  isChatConversationAllowed,
  isAiEnabled,
  isPendingOrHasDraft,
  isAiInteractionAllowed,
  isAiFabVisible,
  isZenMode,
  startedFromSequence,
  logs,
  reasoningLogs,
  hasMessages,
  routeToAgent,
  pendingRouteToAssistant,
  pendingRouteToDesigner,
  pendingRouteToAnalyst,
  pendingRouteToDeveloper,
  pendingRouteToCoordinator,
  pendingRouteToArchitect,
  pendingRouteToIntegrator,
  pendingRouteToAuditor,
  generatingUi,
  generatingPlan,
  selectedPlan,
  currentAITaskMessage,
  documentUploadMessage,
  waitingForHumanApproval,
  generatedIntent,
  generatedActions,
  autonomyLevel,
  getAppId,
  getAppVersionId,
  getProcessId,
  getProcessName,
  processWarningsNo,
  processRules,
  processWarnings,
  selectAutonomyLevel,
  selectGeneratedUINew,
  selectedUiFlowPageIndex,
  currentPlans,
  currentPlanStep,
  hasBusinessProcess,
  getScreenContext,
  pageRouteParams,
  generatedPrompt,
  generatedAgentWorkflow,
  generatedDatasource,
  currentSystemContext,
  designerVersion,
  generatedCustomComponent,
  customComponentContext,
  hasCustomComponentDraft,
}
